// Prompt input box component for AI chat.
// Handles compose mode (textarea + attach + send) and busy mode (waiting status + stop button).

import { PolymerElement, html } from "@polymer/polymer/polymer-element.js";
import { afterNextRender } from "@polymer/polymer/lib/utils/render-status.js";
import "@polymer/polymer/lib/elements/dom-if.js";
import "@polymer/polymer/lib/elements/dom-repeat.js";
import "@polymer/iron-ajax";

import iconSubmitQuestion from "../../../../images/icons/icon_up.svg";
import iconStopChat from "../../../../images/icons/icon_stop.svg";
import iconAttachment from "../../../../images/icons/icon_attachment.svg";
import iconCross from "../../../../images/icons/icon_cross.svg";
import iconLoading from "../../../../images/icons/icon_loading.svg";

import "./PromptInputBox.style";

// File attachment limits and error messages shown to the user.
const MAX_ATTACHMENT_COUNT = 3;
const ATTACHMENT_ERROR_MAX_COUNT = "Maksimal 3 file dapat dilampirkan.";
const ATTACHMENT_ERROR_UNSUPPORTED_TYPE = "Format file tidak didukung. Hanya gambar atau PDF.";
const ATTACHMENT_ERROR_UPLOAD_FAILED = "Gagal mengunggah file. Silakan coba lagi.";

class PromptInputBox extends PolymerElement {
  static get template() {
    return html`
      <style include="prompt-input-box-style"></style>

      <iron-ajax
        id="submitQuestionAjax"
        method="POST"
        url="/api/general/ai/submit_question"
        content-type="application/json"
        handle-as="json"
        body="[[submitQuestionRequestBody]]"
        on-response="_onSubmitQuestionResponse"
        on-error="_onSubmitQuestionError"
      ></iron-ajax>

      <div class="ai-question-input">
        <input
          id="fileInput"
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          hidden
          on-change="_onFileInputChange"
        />

        <template is="dom-if" if="[[composeMode]]">
          <div class$="[[_idleHeaderClass(hadAssistantReply, attachments, isMultiline, isLanding)]]">
            <div class="ai-question-input__composer-row">
              <textarea
                id="textarea"
                class="ai-landing__textarea"
                rows="1"
                placeholder="[[placeholderText]]"
                value="{{value::input}}"
                on-input="_onTextareaInput"
                on-keydown="_handleKeydown"
              ></textarea>

              <div class="ai-landing__actions">
                <template is="dom-if" if="[[showAttachmentButton]]">
                  <button
                    type="button"
                    class="ai-landing__icon-btn"
                    aria-label="Lampirkan file"
                    on-click="_openFilePicker"
                  >
                    <img src="[[iconAttachment]]" alt="" aria-hidden="true" />
                  </button>
                </template>
                <button
                  type="button"
                  class$="[[_sendBtnClass(canSubmit, loading)]]"
                  aria-label="Kirim pertanyaan"
                  disabled$="[[!canSubmit]]"
                  on-click="_submit"
                >
                  <img src="[[iconSubmitQuestion]]" alt="" aria-hidden="true" />
                </button>
              </div>
            </div>

            <template is="dom-if" if="[[_hasAttachments(attachments)]]">
              <div class="ai-question-input__attachments" role="list">
                <template is="dom-repeat" items="[[attachments]]" as="item">
                  <div
                    class$="[[_attachmentPreviewClass(item)]]"
                    uploading$="[[_isUploading(item)]]"
                    role="listitem"
                  >
                    <template is="dom-if" if="[[_isImageAttachment(item)]]" restamp>
                      <img
                        class="ai-question-input__attachment-thumb"
                        src="[[item.previewUrl]]"
                        alt="[[item.name]]"
                      />
                    </template>
                    <template is="dom-if" if="[[_isPdfAttachment(item)]]" restamp>
                      <span class="ai-question-input__attachment-pdf-badge" aria-hidden="true">.pdf</span>
                      <div class="ai-question-input__attachment-pdf-info">
                        <span class="ai-question-input__attachment-pdf-name">[[_truncateFileName(item.name)]]</span>
                        <span class="ai-question-input__attachment-pdf-type">PDF</span>
                      </div>
                    </template>
                    <template is="dom-if" if="[[_isUploading(item)]]">
                      <div
                        class="ai-question-input__attachment-loading"
                        aria-busy="true"
                        aria-label="Mengunggah lampiran"
                      >
                        <img
                          class="ai-question-input__attachment-loading-icon"
                          src="[[iconLoading]]"
                          alt=""
                          aria-hidden="true"
                        />
                      </div>
                    </template>
                    <button
                      type="button"
                      class="ai-question-input__attachment-remove"
                      aria-label="Hapus lampiran"
                      data-id$="[[item.id]]"
                      on-click="_removeAttachment"
                    >
                      <img
                        class="ai-question-input__attachment-remove-icon"
                        src="[[iconCross]]"
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </template>

        <template is="dom-if" if="[[loading]]">
          <div class="ai-question-input__header ai-question-input__header--busy">
            <div class="ai-question-input__busy-row">
              <p class="ai-question-input__busy-status">[[waitingPlaceholderText]]</p>
              <div class="ai-landing__actions">
                <button
                  type="button"
                  class="ai-landing__stop-btn"
                  aria-label="Hentikan"
                >
                  <img src="[[iconStopChat]]" alt="" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <iron-ajax
        id="uploadAttachmentAjax"
        method="POST"
        url="/api/general/ai/upload_attachment"
        handle-as="json"
        debounce-duration="300"
        on-response="_handleUploadAttachmentResponse"
        on-error="_handleUploadAttachmentError"
      ></iron-ajax>
    `;
  }

  static get properties() {
    return {
      value: { type: String, value: "" },
      loading: { type: Boolean, value: false },
      iconSubmitQuestion: {
        type: String,
        value: iconSubmitQuestion,
      },
      iconStopChat: {
        type: String,
        value: iconStopChat,
      },
      iconAttachment: {
        type: String,
        value: iconAttachment,
      },
      iconCross: {
        type: String,
        value: iconCross,
      },
      iconLoading: {
        type: String,
        value: iconLoading,
      },
      composeMode: {
        type: Boolean,
        computed: "_computeComposeMode(loading)",
      },
      canSubmit: {
        type: Boolean,
        computed: "_computeCanSubmit(value, loading, attachments)",
      },
      placeholderText: {
        type: String,
        value: "",
      },
      waitingPlaceholderText: {
        type: String,
        value: "",
      },
      hadAssistantReply: {
        type: Boolean,
        value: false,
      },
      isLanding: {
        type: Boolean,
        value: false,
      },
      isMultiline: {
        type: Boolean,
        value: false,
      },
      showAttachmentButton: {
        type: Boolean,
        value: false,
      },
      topicId: {
        type: String,
        value: "",
      },
      attachments: {
        type: Array,
        value() {
          return [];
        },
      },
      userUid: {
        type: String,
        value: "",
      },
      topicId: {
        type: String,
        value: "",
      },
      submitQuestionRequestBody: {
        type: Object,
      },
      isLogin: {
        type: Number,
        value: false,
      },
      userApproveStatus: {
        type: String,
        value: "",
      },
      aiGeneralCopytext: {
        type: Object,
        value: null,
      },
      maxSizeAttachmentAi: {
        type: Number,
        value: 52428800,
      },
    };
  }

  ready() {
    super.ready();
    afterNextRender(this, () => {
      window.addEventListener('load', () => {
        const isNotReviewed = ["not-reviewed", "not-reviewed-student", ""].includes(this.userApproveStatus);
        const hasSavedPrompt = localStorage.getItem("ai_prompt_text");
        const navigation = performance.getEntriesByType('navigation')[0];
        const hasAttachmentIds = localStorage.getItem("ai_attachment_ids");
        const hasAttachments   = localStorage.getItem("image_attachment") || localStorage.getItem("pdf_attachment");
        const hasParams = localStorage.getItem("has_params");

        if(hasParams){
          return;
        }

        if(!hasSavedPrompt && !hasAttachments && !hasAttachmentIds) return;

        if(isNotReviewed && this.isLogin) {
          if (navigation && navigation.type === 'reload') {
            this._removeLocalStorage();
            return;
          }else{
            this._restorePendingInputFromLocalStorage()
          }
          this._dispatchSubmitQuestionError(
            this.aiGeneralCopytext && this.aiGeneralCopytext.user_ineligible_text,
            'user_approved_status'
          );
          this._removeLocalStorage();
          return;
        }

        if(navigation && navigation.type === 'navigate') {
          this._removeLocalStorage();
          return;
        }

        if(navigation && navigation.type === 'reload' && !this.isLogin && (hasSavedPrompt || hasAttachments || hasAttachmentIds)) {
          this._removeLocalStorage();
          return;
        }

        this._resumePendingSubmitAfterLogin()
      });
    });
  }

  // Check if the user is on the AI page and resume the pending submit after login
  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('load', () => {
      const isNotReviewed = ["not-reviewed", "not-reviewed-student", ""].includes(this.userApproveStatus);
      const hasSavedPrompt = localStorage.getItem("ai_prompt_text");
      const navigation = performance.getEntriesByType('navigation')[0];
      const hasAttachmentIds = localStorage.getItem("ai_attachment_ids");
      const hasAttachments   = localStorage.getItem("image_attachment") || localStorage.getItem("pdf_attachment");
      const params = new URLSearchParams(window.location.search);

      if(params.has('question') || params.has('attachment_id')){
        localStorage.setItem("has_params", true);
        this._submitQuestionFromUrlParams();
        return;
      }

      if(!hasSavedPrompt && !hasAttachments && !hasAttachmentIds) return;
      
      if(isNotReviewed && this.isLogin) {
        if (navigation && navigation.type === 'reload') {
          this._removeLocalStorage();
          return;
        }else{
          this._restorePendingInputFromLocalStorage()
        }        
        this._dispatchSubmitQuestionError(
          this.aiGeneralCopytext && this.aiGeneralCopytext.user_ineligible_text,
          'user_approved_status'
        );
        this._removeLocalStorage();
        return;
      }

      if(navigation && navigation.type === 'navigate') {
        this._removeLocalStorage();
        return;
      }

      if(navigation && navigation.type === 'reload' && !this.isLogin && (hasSavedPrompt || hasAttachments || hasAttachmentIds)) {
        this._removeLocalStorage();
        return;
      }
      
      this._resumePendingSubmitAfterLogin()
    });
  }

  // Remove the local storage
  _removeLocalStorage(){
    localStorage.removeItem("ai_prompt_text");
    localStorage.removeItem("ai_attachment_ids");
    localStorage.removeItem("image_attachment");
    localStorage.removeItem("pdf_attachment"); 
  }

   // Remove submit query params from the URL without reloading the page.
   _clearSubmitParamsFromUrl(skipRemoveLocalStorage = false) {
    if (typeof window === "undefined" || typeof history === "undefined") return;
    history.replaceState(history.state, "", '/ai');
    if(!skipRemoveLocalStorage) {
      this._removeLocalStorage();
    }

    setTimeout(() => {
      localStorage.removeItem("has_params");
    }, 1000);
  }

  // Collect ready attachment IDs from the current preview list.
  _getReadyAttachmentIds() {
    return (this.attachments || [])
      .filter((item) => item && item.status === "ready" && item.attachmentId)
      .map((item) => item.attachmentId);
  }

  // Keep ai_attachment_ids in localStorage aligned with this.attachments.
  _syncAttachmentIdsToLocalStorage() {
    const attachmentIds = this._getReadyAttachmentIds();
    if (attachmentIds.length > 0) {
      localStorage.setItem("ai_attachment_ids", attachmentIds.join(","));
    } else {
      localStorage.removeItem("ai_attachment_ids");
    }
  }

  // Find one saved attachment record by attachment_id.
  _findStoredAttachment(attachmentId) {
    const keys = ["image_attachment", "pdf_attachment"];

    for (let i = 0; i < keys.length; i++) {
      const raw = localStorage.getItem(keys[i]);
      if (!raw) continue;

      try {
        let list = JSON.parse(raw);
        list = Array.isArray(list) ? list : [list];
        const found = list.find(
          (stored) => String(stored.attachment_id) === String(attachmentId)
        );
        if (found) return found;
      } catch (parseError) {
        console.error(`[PromptInputBox] Invalid ${keys[i]}:`, parseError);
      }
    }

    return null;
  }

  _buildAttachmentFromStored(stored, idPrefix) {
    return {
      id: `${idPrefix}-${stored.id || stored.attachment_id}`,
      name: stored.name || "file",
      kind: stored.type === "image" ? "image" : "pdf",
      previewUrl: stored.type === "image" ? (stored.url || "") : "",
      attachmentId: String(stored.attachment_id || ""),
      status: "ready",
    };
  }

  // Build attachment previews from URL attachment_id values and localStorage metadata.
  _buildAttachmentsFromUrlIds(attachmentIds) {
    return attachmentIds
      .map((attachmentId) => {
        const stored = this._findStoredAttachment(attachmentId);
        if (!stored || !stored.type) return null;

        return this._buildAttachmentFromStored(stored, "att-url");
      })
      .filter(Boolean);
  }

   // Auto-submit when user arrives from home with question in the URL.
   _submitQuestionFromUrlParams() {
    if (typeof window === "undefined") return null;

    const params = new URLSearchParams(window.location.search);
    const question = (params.get("question") || "").trim();
    const attachmentIds = params
    .getAll("attachment_id")
    .flatMap(function (value) {
      return String(value).split(",").map(function (id) { return id.trim(); });
    })
    .filter(Boolean);
    
    if (!question && attachmentIds.length === 0) return;

    if (question) {
      this.setValue(question);
      localStorage.setItem("ai_prompt_text", question);
    }

    if (attachmentIds.length > 0) {
      this.attachments = this._buildAttachmentsFromUrlIds(attachmentIds);
    } else {
      this.attachments = [];
    }

    this._syncAttachmentIdsToLocalStorage();
    this._onTextareaInput();

    if(this.isLogin){
      if(!["not-reviewed", "not-reviewed-student"].includes(this.userApproveStatus)){
        this.hadAssistantReply = true;
        this.isMultiline = false;
      }
      this._clearSubmitParamsFromUrl(false);
      this._submit();
      return;
    }

    this._openLoginOverlay();
    this._clearSubmitParamsFromUrl(true);
  }

  // After login, resend the question saved in localStorage before the user signed in.
  _resumePendingSubmitAfterLogin() {
    if (!this.isLogin) return;

    const promptText = (localStorage.getItem("ai_prompt_text") || "").trim();
    if (!promptText) return;

    if (["not-reviewed", "not-reviewed-student"].includes(this.userApproveStatus)) {
      this._dispatchSubmitQuestionError(
        this.aiGeneralCopytext && this.aiGeneralCopytext.user_ineligible_text,
        'user_approved_status'
      );
      return;
    }

    this._restorePendingInputFromLocalStorage();
    const restoredPromptText = (this.value || "").trim() || promptText;
    const attachmentIds = this._getReadyAttachmentIds();

    this.dispatchEvent(
      new CustomEvent("ai-submit", {
        bubbles: true,
        composed: true,
        detail: {
          value: restoredPromptText,
          attachmentIds,
          isResumeAfterLogin: true,
        },
      })
    );

    this._removeLocalStorage();
    this.resubmitQuestion(restoredPromptText, attachmentIds);
  }

  // Tell the AI page to show a file attachment error message.
  _showAttachmentError(message) {
    this.dispatchEvent(
      new CustomEvent("attachment-error", {
        bubbles: true,
        composed: true,
        detail: { message: message || "" },
      })
    );
  }

  // Build header CSS class (row by default; column only when file previews are shown).
  _idleHeaderClass(hadAssistantReply, attachments, isMultiline, isLanding) {
    let headerClass =
      "ai-question-input__header ai-question-input__header--idle";
    if (hadAssistantReply) {
      headerClass += " ai-question-input__header--idle-post-reply";
      if (isMultiline) {
        headerClass += " ai-question-input__header--idle-multiline";
      }
    } else {
      headerClass += " ai-question-input__header--idle-welcome";
      // Horizontal offset only on landing; chatting (incl. failed submit) stays centered.
      if (isLanding) {
        headerClass += " ai-question-input__header--idle-welcome-landing";
      }
    }
    if (attachments && attachments.length > 0) {
      headerClass += " ai-question-input__header--idle--with-attachments";
    }
    return headerClass;
  }

  // Compose mode is the inverse of loading state.
  _computeComposeMode(loading) {
    return !loading;
  }

  // User can submit when not loading and has non-empty text or ready attachments or uploading attachments.
  _computeCanSubmit(value, loading, attachments) {
    if (loading) return false;

    // check if the value and attachments is not empty
    const hasText = !!(value || "").trim();
    const attachmentList = attachments || [];

    // check if the attachments is not empty
    if (attachmentList.length > 0) {
      const hasReadyFiles = attachmentList.some(
        (item) => item && item.status === "ready" && item.attachmentId
      );
      const isUploading = attachmentList.some((item) => item && item.status === "uploading");

      // check if the attachments is uploading
      if (isUploading) return false;

      // check if the attachments is ready
      return hasText && hasReadyFiles;
    }

    // check if the value is not empty
    return hasText;
  }

  // Build send button CSS class: active only when submission is allowed.
  _sendBtnClass(canSubmit, loading) {
    let sendButtonClass = "ai-landing__send-btn";
    if (canSubmit && !loading) sendButtonClass += " is-active";
    return sendButtonClass;
  }

  // Returns true when at least one attachment is shown in the preview bar.
  _hasAttachments(attachments) {
    return Array.isArray(attachments) && attachments.length > 0;
  }

  // Returns true when the attachment item is an image preview.
  _isImageAttachment(item) {
    return !!(item && item.kind === "image");
  }

  // Returns true when the attachment item is a PDF preview.
  _isPdfAttachment(item) {
    return !!(item && item.kind === "pdf");
  }

  // Returns preview wrapper class for image (60×60) or PDF (212×60) layout.
  _attachmentPreviewClass(item) {
    const base = "ai-question-input__attachment-preview";
    if (item && item.kind === "pdf") {
      return `${base} ai-question-input__attachment-preview--pdf`;
    }
    if (item && item.kind === "image") {
      return `${base} ai-question-input__attachment-preview--image`;
    }
    return base;
  }

  // Returns true while the file is still uploading.
  _isUploading(item) {
    return !!(item && item.status === "uploading");
  }

  // Grow textarea in post-reply mode; with attachments, let CSS handle height.
  _onTextareaInput() {
    const textarea = this.shadowRoot && this.shadowRoot.querySelector("#textarea");

    if (!textarea) return;
    if (!this.hadAssistantReply) return;

    textarea.style.height = "auto";
    const naturalHeight = textarea.scrollHeight;
    const text = textarea.value || "";

    const SINGLE_LINE_TEXTAREA_HEIGHT_PX = 24;

    this.isMultiline =
      text.trim().length > 0 &&
      (text.includes("\n") || naturalHeight > SINGLE_LINE_TEXTAREA_HEIGHT_PX);

    if (this._hasAttachments(this.attachments)) {
      textarea.style.removeProperty("height");
      return;
    }

    if (!this._hasAttachments(this.attachments) && !this.isMultiline) {
      textarea.style.removeProperty("height");
      return;
    }

    const MAX_TEXTAREA_HEIGHT_PX = 132;

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }

  // Shorten long file names shown on the PDF preview.
  _truncateFileName(fileName) {
    const MAX_DISPLAY_FILE_NAME_LENGTH = 19;

    const name = fileName != null ? String(fileName) : "";
    if (name.length <= MAX_DISPLAY_FILE_NAME_LENGTH) return name;
    return `${name.slice(0, MAX_DISPLAY_FILE_NAME_LENGTH)}...`;
  }

  // Check file type: only JPEG, PNG images or PDF.
  _getAttachmentKind(file) {
    if (!file || !file.type) return null;
    if (file.type === "image/jpeg" || file.type === "image/png") return "image";
    if (file.type === "application/pdf") return "pdf";
    return null;
  }

  // Send one attachment file to the upload API.
  _startAttachmentUpload(file, localPreviewId) {
    const uploadAjax = this.$ && this.$.uploadAttachmentAjax;
    if (!uploadAjax) {
      this._removeAttachmentPreview(localPreviewId);
      return;
    }

    const kind = this._getAttachmentKind(file);
    const fileType = kind === "image" ? "image" : "document";

    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);
    formData.append("file_name", file.name);
    if (this.topicId) formData.append("topic_id", this.topicId);
    if (this.userUid) formData.append("user_uid", this.userUid);

    const csrfMeta = document.head.querySelector("meta[name='csrf-token']");
    const csrfToken = csrfMeta && csrfMeta.content ? csrfMeta.content : "";
    this._currentUploadPreviewId = localPreviewId;
    uploadAjax.headers = csrfToken ? { "X-CSRF-Token": csrfToken } : {};
    uploadAjax.body = formData;
    uploadAjax.generateRequest();
  }

  _addAttachmentToLocalStorage(responseBody) {
    const data = responseBody && responseBody.data;
    if (!data) return;
  
    const storageKey = data.type === "image" ? "image_attachment" : "pdf_attachment";
    if (data.type !== "image" && data.type !== "document") return;
  
    let list = [];
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        list = Array.isArray(parsed) ? parsed : [parsed];
      } catch (parseError) {
        console.error(`[PromptInputBox] Invalid ${storageKey}:`, parseError);
      }
    }
  
    list.push(Object.assign({}, data, { id: list.length + 1 }));
    localStorage.setItem(storageKey, JSON.stringify(list));
  }

  _restorePendingInputFromLocalStorage() {
    const promptText = (localStorage.getItem("ai_prompt_text") || "").trim();
    if (promptText) this.setValue(promptText);
  
    let storedList = [];
    ["image_attachment", "pdf_attachment"].forEach((key) => {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        storedList = storedList.concat(Array.isArray(parsed) ? parsed : [parsed]);
      } catch (parseError) {
        console.error(`[PromptInputBox] Invalid ${key}:`, parseError);
      }
    });
  
    if (storedList.length === 0) return [];
  
    this.attachments = storedList.map((stored) =>
      this._buildAttachmentFromStored(stored, "att-restored")
    );
    this._syncAttachmentIdsToLocalStorage();
    this._onTextareaInput();

    return this._getReadyAttachmentIds();
  }

  // Save attachment_id on the preview item after a successful upload response.
  _handleUploadAttachmentResponse(event) {
    // get the local preview id
    const localPreviewId = this._currentUploadPreviewId;
    const responseBody = event && event.detail && event.detail.response;

    // check if the local preview id is not empty
    if (!localPreviewId) return;
    
    // check if the response body is not empty and the status is not 200
    if (!responseBody || responseBody.status !== 200) {
      const errorMessage =
        (responseBody && responseBody.data && responseBody.data.message) ||
        (responseBody && responseBody.message) ||
        ATTACHMENT_ERROR_UPLOAD_FAILED;
      this._showAttachmentError(String(errorMessage));
      this._removeAttachmentPreview(localPreviewId);
      this._currentUploadPreviewId = "";
      return;
    }

    if(!this.isLogin){
      this._addAttachmentToLocalStorage(responseBody);
    }

    const attachmentId =
      responseBody.data && responseBody.data.attachment_id != null
        ? String(responseBody.data.attachment_id)
        : "";

    if (!attachmentId) {
      const errorMessage =
        (responseBody && responseBody.data && responseBody.data.message) ||
        ATTACHMENT_ERROR_UPLOAD_FAILED;
      this._showAttachmentError(String(errorMessage));
      this._removeAttachmentPreview(localPreviewId);
      this._currentUploadPreviewId = "";
      return;
    }

    this.attachments = (this.attachments || []).map((entry) =>
      entry.id === localPreviewId
        ? Object.assign({}, entry, { status: "ready", attachmentId })
        : entry
    );

    if (!this.isLogin) {
      this._syncAttachmentIdsToLocalStorage();
    }

    this._currentUploadPreviewId = "";
  }

  // Remove the preview item when the upload request fails.
  _handleUploadAttachmentError() {
    const localPreviewId = this._currentUploadPreviewId;
    this._showAttachmentError(ATTACHMENT_ERROR_UPLOAD_FAILED);

    if (localPreviewId) this._removeAttachmentPreview(localPreviewId);
    this._currentUploadPreviewId = "";
  }

  _removeAttachmentPreview(localPreviewId) {
    this.attachments = (this.attachments || []).filter((entry) => entry.id !== localPreviewId);
  }

  // Open the hidden file picker.
  _openFilePicker() {
    // get the file input
    const fileInput = this.$ && this.$.fileInput;
    if (!fileInput) return;

    // Stop opening the file picker when 3 files are already attached.
    if ((this.attachments || []).length >= MAX_ATTACHMENT_COUNT) {
      this._showAttachmentError(ATTACHMENT_ERROR_MAX_COUNT);
      return;
    }

    fileInput.value = "";

    // browser will automatically click the file input
    fileInput.click();
  }

  // Handle the file chosen from the native file picker.
  _onFileInputChange(event) {
    const fileInput = event && event.target;
    const file = fileInput && fileInput.files && fileInput.files[0];
    if (!file) return;

    // Only add as many files as still allowed; warn if the user picks too many.
    const currentCount = (this.attachments || []).length;
    const remainingSlots = MAX_ATTACHMENT_COUNT - currentCount;
    if (remainingSlots <= 0) {
      this._showAttachmentError(ATTACHMENT_ERROR_MAX_COUNT);
      return;
    }

    this._addLocalAttachment(file);
  }

  // Show file preview, upload via API, then save attachment_id on that preview.
  _addLocalAttachment(file) {
    const hasUploadInProgress = (this.attachments || []).some(
      (item) => item && item.status === "uploading"
    );
    if (hasUploadInProgress) return;

    if (!file) return;

    // Check file count, size, and type before showing preview and uploading.
    if ((this.attachments || []).length >= MAX_ATTACHMENT_COUNT) {
      this._showAttachmentError(ATTACHMENT_ERROR_MAX_COUNT);
      return;
    }

    const maxSizeBytes = this.maxSizeAttachmentAi || 52428800;

    if (file.size > maxSizeBytes) {
      const sizeMb = Math.round(maxSizeBytes / (1024 * 1024));
      const attacmentErrorMaxSize = `Ukuran file melebihi ${sizeMb}MB.`;

      this._showAttachmentError(attacmentErrorMaxSize);
      return;
    }

    const kind = this._getAttachmentKind(file);
    if (!kind) {
      console.warn("[PromptInputBox] Unsupported file type:", file && file.type);
      this._showAttachmentError(ATTACHMENT_ERROR_UNSUPPORTED_TYPE);
      return;
    }

    const localPreviewId = `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const item = {
      id: localPreviewId,
      name: file.name || "file",
      kind,
      previewUrl: "",
      attachmentId: "",
      status: "uploading",
    };

    const startUpload = () => {
      this._startAttachmentUpload(file, localPreviewId);
    };

    if (kind === "image") {
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        item.previewUrl = (loadEvent.target && loadEvent.target.result) || "";
        this.attachments = [...(this.attachments || []), item];
        this._onTextareaInput()
        startUpload();
      };
      reader.onerror = (readError) => {
        console.error("[PromptInputBox] Failed to read image preview:", readError);
        // Tell the AI page the image preview could not be loaded.
        this._showAttachmentError(ATTACHMENT_ERROR_UPLOAD_FAILED);
      };
      reader.readAsDataURL(file);
      return;
    }

    this.attachments = [...(this.attachments || []), item];
    startUpload();
  }

  // Remove one saved attachment from localStorage before login.
  _removeStoredAttachment(removedItem) {
    if (this.isLogin || !removedItem) return;

    const storageKeys =
      removedItem.kind === "image"
        ? ["image_attachment"]
        : removedItem.kind === "pdf"
          ? ["pdf_attachment"]
          : ["image_attachment", "pdf_attachment"];

    storageKeys.forEach((storageKey) => {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;

      try {
        let list = JSON.parse(raw);
        list = Array.isArray(list) ? list : [list];
        list = list.filter((stored) => {
          if (removedItem.attachmentId) {
            return String(stored.attachment_id) !== String(removedItem.attachmentId);
          }
          const storageId = String(removedItem.id).replace("att-restored-", "");
          return String(stored.id) !== storageId;
        });

        if (list.length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(list));
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (parseError) {
        console.error(`[PromptInputBox] Invalid ${storageKey}:`, parseError);
      }
    });
  }

  // Remove one file preview when the user clicks ×.
  _removeAttachment(event) {
    const attachmentId =
      event &&
      event.currentTarget &&
      event.currentTarget.dataset &&
      event.currentTarget.dataset.id;
    if (!attachmentId) return;

    const removedItem = (this.attachments || []).find((item) => item.id === attachmentId);
    if (removedItem) {
      this._removeStoredAttachment(removedItem);
    }

    this.attachments = (this.attachments || []).filter((item) => item.id !== attachmentId);

    if (!this.isLogin) {
      this._syncAttachmentIdsToLocalStorage();
    }

    this._onTextareaInput();
  }

  // Focus the textarea programmatically (called by parent after suggestion prompt pick).
  focusTextarea() {
    if (this.loading) return;

    const textarea = this.$ && this.$.textarea;
    if (textarea && typeof textarea.focus === "function") textarea.focus();
  }

  // Set the textarea value programmatically (called by parent after submit or suggestion prompt pick).
  setValue(nextValue) {
    this.value = nextValue || "";
    this._onTextareaInput()  
  }

  // Clear all file previews (called by parent after successful submit).
  clearAttachments() {
    this.attachments = [];
  }

  // Submit on Enter (without Shift), prevent default newline.
  _handleKeydown(keyboardEvent) {
    if (this.loading) return;
    if (keyboardEvent.key === "Enter" && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this._submit();
    }
  }

  // Set CSRF header on submit question request (same pattern as payment iron-ajax).
  _setSubmitQuestionCsrfHeader() {
    const csrfMeta = document.head.querySelector("meta[name='csrf-token']");
    const submitQuestionAjax = this.$ && this.$.submitQuestionAjax;
    if (!csrfMeta || !submitQuestionAjax) return;

    if (!submitQuestionAjax.headers) {
      submitQuestionAjax.headers = {};
    }
    submitQuestionAjax.headers["X-CSRF-Token"] = csrfMeta.content;
  }

  // POST submit_question without dispatching ai-submit (used when parent retries a failed send).
  resubmitQuestion(question, attachmentIds = []) {
    const submitQuestionValue = (question || "").trim();
    if (!submitQuestionValue) return;

    this._sendSubmitQuestionRequest(submitQuestionValue, attachmentIds || []);
  }

  // Build request body and call submit_question API.
  _sendSubmitQuestionRequest(question, attachmentIds) {
    this.submitQuestionRequestBody = {
      user_uid: this.userUid,
      topic_id: this.topicId,
      question,
      attachment_ids: attachmentIds,
    };

    this._setSubmitQuestionCsrfHeader();
    this.$.submitQuestionAjax.generateRequest();
  }

  // Dispatch submit event with question text and ready attachment IDs.
  _submit() {
    if(["not-reviewed", "not-reviewed-student"].includes(this.userApproveStatus)) {
      this._dispatchSubmitQuestionError(
        this.aiGeneralCopytext && this.aiGeneralCopytext.user_ineligible_text,
        'user_approved_status'
      );
      return;
    }

    if (!this.canSubmit) return;

    const attachmentIds = this._getReadyAttachmentIds();
    const submitQuestionValue = (this.value || "").trim();

    if (!this.isLogin) {
      localStorage.setItem("ai_prompt_text", submitQuestionValue);
      this._syncAttachmentIdsToLocalStorage();
      this._openLoginOverlay();
      return;
    }

    // dispatch the submit event
    this.dispatchEvent(
      new CustomEvent("ai-submit", {
        bubbles: true,
        composed: true,
        detail: {
          value: submitQuestionValue,
          attachmentIds,
        },
      })
    );

    this._sendSubmitQuestionRequest(submitQuestionValue, attachmentIds);
  }

  // Send ai-submit-error to parent so AIPage can show error display.
  _dispatchSubmitQuestionError(message, type = 'general') {
    this.dispatchEvent(
      new CustomEvent("ai-submit-error", {
        bubbles: true,
        composed: true,
        detail: {
          message: String(message || "Terjadi kendala. Silakan coba lagi."),
          type: type
        },
      })
    );
  }

  // Handle iron-ajax response; HTTP 4xx/5xx go to error display (on-error is not called for those).
  _onSubmitQuestionResponse(event) {
    const responseBody = event.detail.response;

    if (responseBody.status !== 200) {
      const message = responseBody.message || "Terjadi kendala. Silakan coba lagi.";
      this._dispatchSubmitQuestionError(message);
      return;
    }

    this._onTextareaInput();

    // Prepare the submit success event
    this.dispatchEvent(
      new CustomEvent("ai-submit-success", {
        bubbles: true,
        composed: true,
        detail: { responseBody },
      })
    );
  }

  // Handle iron-ajax on-error when request cannot reach server (network or transport failure).
  _onSubmitQuestionError() {
    this._dispatchSubmitQuestionError();
  }

  
  // Show the login modal overlay (legacy jQuery entrance-overlay).
  _openLoginOverlay() {
    try {
      if (window.$) window.$("#entrance-overlay").css("display", "block");
    } catch (overlayError) {
      console.error("[_openLoginOverlay] Failed to show login overlay:", overlayError);
    }
  }
}

customElements.define("prompt-input-box", PromptInputBox);
